/* Club 24: native interactions, progressive enhancement and reduced-motion support. */
(() => {
  'use strict';
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const dialogStates = new WeakMap();
  const images = new WeakMap();
  const fades = new WeakMap();
  function fade(element, distance = 4) {
    if (!element || reduce.matches || !element.animate) return;
    fades.get(element)?.cancel();
    const animation = element.animate([
      { opacity: .2, transform: `translateY(${distance}px)` },
      { opacity: 1, transform: 'translateY(0)' }
    ], { duration: 150, easing: ease });
    fades.set(element, animation);
  }
  function openDialog(dialog) {
    let state = dialogStates.get(dialog);
    if (!state) { state = {}; dialogStates.set(dialog, state); }
    clearTimeout(state.timer);
    if (!dialog.open) {
      state.trigger = document.activeElement;
      dialog.classList.remove('is-open', 'is-closing');
      dialog.classList.add('c24-motion-dialog');
      if (dialog.classList.contains('lightbox')) dialog.classList.add('t-modal');
      dialog.showModal();
      // Establish the entry frame while keeping native focus trapping and backdrop.
      void dialog.offsetWidth;
    }
    dialog.classList.remove('is-closing');
    dialog.classList.add('is-open');
    document.documentElement.classList.add('c24-dialog-open');
  }
  function closeDialog(dialog) {
    if (!dialog.open || dialog.classList.contains('is-closing')) return;
    const state = dialogStates.get(dialog) || {};
    clearTimeout(state.timer);
    dialog.classList.remove('is-open');
    dialog.classList.add('is-closing');
    const done = () => {
      dialog.close();
      dialog.classList.remove('is-closing');
      if (!document.querySelector('.c24-motion-dialog[open]')) document.documentElement.classList.remove('c24-dialog-open');
      if (state.trigger?.isConnected) state.trigger.focus({ preventScroll: true });
    };
    if (reduce.matches) done();
    else state.timer = setTimeout(done, dialog.classList.contains('fit-drawer') ? 350 : 150);
    dialogStates.set(dialog, state);
  }
  async function swapImage(image, source, alt) {
    const token = {};
    const previous = images.get(image);
    previous?.animation?.cancel();
    previous?.overlay?.remove();
    images.set(image, token);
    const next = new Image();
    next.src = source;
    try { await next.decode(); } catch { return false; }
    if (images.get(image) !== token) return false;
    // Keep the old, decoded image over the new one until the crossfade completes.
    const overlay = image.cloneNode();
    overlay.removeAttribute('id');
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.className = 'c24-image-out';
    image.removeAttribute('srcset');
    image.src = source;
    image.alt = alt;
    if (!reduce.matches && overlay.animate) {
      image.parentElement.append(overlay);
      token.overlay = overlay;
      token.animation = overlay.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 250, easing: ease });
      token.animation.finished.catch(() => {}).finally(() => overlay.remove());
    }
    return true;
  }
  let accordionId = 0;
  function enhanceAccordions(scope = document) {
    scope.querySelectorAll('#c24-pdp .accordions details, #CartDrawer details.c24-express').forEach(details => {
      const summary = details.querySelector(':scope > summary');
      if (!summary) return;
      const item = document.createElement('div');
      item.className = details.className + ' t-acc';
      item.dataset.open = String(details.open);
      const head = document.createElement('button');
      head.type = 'button'; head.className = 't-acc-head';
      head.textContent = summary.childNodes[0].textContent.trim();
      head.setAttribute('aria-expanded', String(details.open));
      const chevron = document.createElement('span');
      chevron.className = 't-acc-chevron'; chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = '<svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor"><path d="M4 6.5L8 10.5L12 6.5"/></svg>';
      head.append(chevron);
      const panel = document.createElement('div'); panel.className = 't-acc-panel';
      panel.id = 'c24-disclosure-' + ++accordionId;
      head.setAttribute('aria-controls', panel.id);
      const inner = document.createElement('div'); inner.className = 't-acc-panel-inner';
      inner.inert = !details.open;
      const body = document.createElement('div'); body.className = 'c24-acc-body';
      [...details.childNodes].filter(node => node !== summary).forEach(node => body.append(node));
      inner.append(body); panel.append(inner); item.append(head, panel); details.replaceWith(item);
      head.addEventListener('click', () => {
        const open = item.dataset.open !== 'true';
        item.dataset.open = String(open);
        head.setAttribute('aria-expanded', String(open));
        inner.inert = !open;
      });
    });
  }
  window.C24Motion = { openDialog, closeDialog, swapImage, fade, reduced: () => reduce.matches };
  function init() {
    enhanceAccordions();
    document.querySelectorAll('#c24-pdp dialog').forEach(dialog => {
      dialog.addEventListener('cancel', event => { event.preventDefault(); closeDialog(dialog); });
    });
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    const header = drawer.querySelector('.drawer__header');
    const status = document.createElement('p');
    status.className = 'c24-cart-confirmation'; status.setAttribute('role', 'status');
    header?.append(status);
    document.addEventListener('ajaxProduct:added', () => {
      status.replaceChildren();
      const check = document.createElement('span');
      check.className = 't-success-check'; check.dataset.state = 'in'; check.setAttribute('aria-hidden', 'true');
      check.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6"><path pathLength="20" d="m5 12 4 4 10-10"/></svg>';
      status.append(check, document.createTextNode(document.documentElement.lang === 'nl' ? 'Toegevoegd aan je selectie.' : 'Added to your selection.'));
    });
    let wasOpen = drawer.classList.contains('drawer--is-open');
    new MutationObserver(records => {
      if (records.some(record => record.type === 'childList')) enhanceAccordions(drawer);
      if (records.some(record => record.type === 'attributes')) {
        const isOpen = drawer.classList.contains('drawer--is-open');
        if (wasOpen && !isOpen) status.replaceChildren();
        wasOpen = isOpen;
      }
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  reduce.addEventListener('change', () => {
    if (!reduce.matches) return;
    document.querySelectorAll('#c24-pdp *, #CartDrawer *').forEach(element => element.getAnimations().forEach(animation => animation.finish()));
  });
})();
